export type LegalDoc = 'privacy' | 'terms';

export interface LegalSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface LegalDocument {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
}

export const CONTACT_EMAIL = 'hello@holistick.app';

const UPDATED = '18 October 2026';

export const LEGAL_DOCUMENTS: Record<LegalDoc, LegalDocument> = {
  privacy: {
    title: 'Privacy policy',
    updated: UPDATED,
    intro:
      'Holistick is a habit, task and notes tracker for the web and iPhone. This policy explains what we collect, why, where it is stored and how you can delete it.',
    sections: [
      {
        heading: 'What we collect',
        bullets: [
          'Account details: your email address and an account ID. If you sign in with Google or Apple, we also receive the name and email you share from that account. With Apple you can choose to hide your email, in which case we only see an Apple relay address.',
          'Content you add: habits and categories, check-ins and ratings, tasks, daily notes, monthly focus, life-area ratings and your app preferences (language, theme, layout).',
          'Google Calendar access, only if you connect it. The access tokens are stored encrypted and are used only to show and create events you ask for.',
          'Technical data: our hosting providers log IP addresses and request metadata to keep the service running and secure. If error monitoring is enabled, crash reports contain technical details, not your notes or tasks.',
        ],
      },
      {
        heading: 'What we do not do',
        bullets: [
          'We do not sell your data or share it with advertisers.',
          'We do not track you across other apps or websites.',
          'We do not use your content to train AI models.',
        ],
      },
      {
        heading: 'How we use it',
        paragraphs: [
          'Only to provide Holistick: signing you in, syncing your data between devices, showing your progress and statistics, and sending the reminders you set. Reminders on iPhone are scheduled on your device.',
        ],
      },
      {
        heading: 'Where it is stored and who processes it',
        bullets: [
          'Supabase: database and sign-in.',
          'Render: hosts the Holistick API.',
          'Cloudflare: hosts the Holistick website.',
          'Google: only if you sign in with Google or connect Google Calendar.',
          'Apple: only if you sign in with Apple.',
        ],
        paragraphs: [
          'Data is encrypted in transit (HTTPS) and at rest by these providers. Task titles and some note fields are additionally encrypted by Holistick before they are written to the database. Some providers may store data outside your country.',
        ],
      },
      {
        heading: 'Who can see your data',
        paragraphs: [
          'Your data is private to your account. The people who run Holistick can technically access the database to operate the service. We only do so when needed for support, security or legal reasons.',
        ],
      },
      {
        heading: 'Keeping and deleting your data',
        paragraphs: [
          'We keep your data while your account exists. You can delete your account at any time from Settings → Delete account, in the app or on the web. Your account is then closed straight away and permanently deleted, with everything in it, after 30 days. If you sign in during those 30 days you can restore it. Copies in our providers\u2019 backups expire on their regular backup schedule.',
        ],
      },
      {
        heading: 'Your rights',
        paragraphs: [
          `You can ask to access, correct, export or delete your personal information by emailing ${CONTACT_EMAIL}. Depending on where you live, laws such as the New Zealand Privacy Act 2020 or the GDPR give you these rights, and the right to complain to your local privacy regulator.`,
        ],
      },
      {
        heading: 'Children',
        paragraphs: [
          'Holistick is not directed at children under 13, and we do not knowingly collect their information.',
        ],
      },
      {
        heading: 'Changes',
        paragraphs: [
          'If we change this policy, we will update the date above and, for significant changes, tell you in the app.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [`Questions about privacy: ${CONTACT_EMAIL}`],
      },
    ],
  },
  terms: {
    title: 'Terms of use',
    updated: UPDATED,
    intro: 'These terms apply when you use Holistick on the web or on iPhone. By creating an account or using the app, you agree to them.',
    sections: [
      {
        heading: 'Your account',
        paragraphs: [
          'You need an email address or Google account to sign in. Keep access to that email secure, because sign-in codes are sent there. You are responsible for activity on your account.',
        ],
      },
      {
        heading: 'Your content',
        paragraphs: [
          'You own the habits, tasks and notes you add. You give us permission to store and process them only so we can provide the service to you. You can delete your content or your whole account at any time.',
        ],
      },
      {
        heading: 'Acceptable use',
        bullets: [
          'Do not try to access other people\u2019s accounts or data.',
          'Do not disrupt, overload or reverse-engineer the service.',
          'Do not use Holistick for anything unlawful.',
        ],
      },
      {
        heading: 'Not professional advice',
        paragraphs: [
          'Holistick helps you track habits and reflect. It is not medical, mental-health or other professional advice.',
        ],
      },
      {
        heading: 'Availability',
        paragraphs: [
          'We work to keep Holistick reliable, but it is provided \u201cas is\u201d, without guarantees that it will always be available or error-free. We may change or discontinue features. If we shut the service down, we will give reasonable notice so you can export your data.',
        ],
      },
      {
        heading: 'Liability',
        paragraphs: [
          'To the extent permitted by law, we are not liable for indirect or consequential loss, or for loss of data. Nothing in these terms limits rights you have under consumer law that cannot be excluded, such as the New Zealand Consumer Guarantees Act where it applies.',
        ],
      },
      {
        heading: 'Ending your use',
        paragraphs: [
          'You can stop using Holistick and delete your account at any time. We may suspend accounts that break these terms.',
        ],
      },
      {
        heading: 'Governing law',
        paragraphs: ['These terms are governed by the laws of New Zealand.'],
      },
      {
        heading: 'Changes and contact',
        paragraphs: [
          `If we change these terms, we will update the date above. Questions: ${CONTACT_EMAIL}`,
        ],
      },
    ],
  },
};
